import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, Zap, Shield, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const coreFeatures = [
  'AI Assistant with natural language CRM',
  'Zero-touch CRM updates from meetings, chats, images',
  'Meeting Intelligence (Google Meet, Zoom, Teams)',
  'AI meeting prep and post-meeting actions',
  'Pipeline health scoring (choose your methodology: MEDDIC, BANT, SPICED...)',
  'Buyer engagement tracking and going-dark alerts',
  'Revenue Command Center for CROs',
  'Monday Morning Digest emails',
  'HubSpot, Salesforce, Pipedrive integrations',
  'Google Workspace, Slack, Zoom integrations',
  'Three-tier RBAC (CRO, Manager, Rep)',
  'GDPR compliant, EU-hosted',
];


export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');

  const scrollToDemo = () => {
    const el = document.getElementById('book-demo');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else navigate(isFrench ? '/fr?scrollTo=demo' : '/?scrollTo=demo');
  };

  return (
    <Layout>
      <SEOHead
        title="Pricing | NoQ5.ai - AI Chief of Staff for Revenue Teams"
        description="NoQ5 Core at EUR 59/user/month. Built for sales-driven teams. Contact us for a personalised demo."
        canonicalUrl="https://noq5.ai/pricing"
        keywords="NoQ5 pricing, AI CRM pricing, revenue intelligence pricing, sales AI pricing"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Does NoQ5 replace my CRM?", "acceptedAnswer": { "@type": "Answer", "text": "No. It makes your existing CRM 10x more valuable by keeping it perfectly updated. Works with HubSpot, Salesforce, and Pipedrive." }},
            { "@type": "Question", "name": "How long does setup take?", "acceptedAnswer": { "@type": "Answer", "text": "Connect your CRM in 5 minutes. Full team onboarding in under a day." }},
            { "@type": "Question", "name": "Is my data safe?", "acceptedAnswer": { "@type": "Answer", "text": "AES-256 encryption, row-level tenant isolation, GDPR controls, and configurable data retention. EU-hosted." }},
            { "@type": "Question", "name": "What CRMs do you support?", "acceptedAnswer": { "@type": "Answer", "text": "HubSpot, Salesforce, and Pipedrive with identical feature parity across all three." }},
            
            { "@type": "Question", "name": "How do I get started?", "acceptedAnswer": { "@type": "Answer", "text": "Contact us for a personalised demo. We offer trials on request so you can see the platform in action with your own data." }},
          ]
        }]}
      />
      <Breadcrumbs items={[{ label: "Pricing", href: "/pricing" }]} />

      <div className="py-12 md:py-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            One platform. One plan. Works with or without a CRM.
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-6">
            No CRM yet? <a href="/crm-optional" className="text-primary underline font-semibold">Run NoQ5 standalone</a> with a live Google Drive mirror — move to HubSpot, Salesforce or Pipedrive whenever you're ready.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-medium">Trials available on request</span>
          </div>
        </motion.div>

        {/* Two-tier pricing */}
        <div className="max-w-xl mx-auto px-4 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="border-2 border-primary shadow-xl relative overflow-hidden flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-primary/60" />
              <CardHeader className="text-center pt-8">
               <CardTitle className="text-2xl font-bold">NoQ5 Core</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">The Zero-Touch CRM Intelligence Platform</p>
                <div className="mt-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-foreground">EUR 59</span>
                    <span className="text-lg text-muted-foreground">/user/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">12-month commitment, billed annually</p>
                </div>
              </CardHeader>
              <CardContent className="px-6 flex-1">
                <ul className="space-y-3">
                  {coreFeatures.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex flex-col gap-3 px-6 pb-8">
                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6 font-bold"
                  onClick={scrollToDemo}
                >
                  Contact Us <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">Trials available on request</p>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto px-4 mb-20"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-sm">
              <Shield className="w-10 h-10 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">GDPR Compliant</h3>
                <p className="text-sm text-muted-foreground">EU-hosted, AES-256 encryption</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-sm">
              <Clock className="w-10 h-10 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">5-Minute Setup</h3>
                <p className="text-sm text-muted-foreground">Connect your CRM and go</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6 bg-card border border-border rounded-sm">
              <Zap className="w-10 h-10 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">Volume Discounts</h3>
                <p className="text-sm text-muted-foreground">Custom pricing for 15+ users</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-3xl mx-auto px-4"
        >
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Does NoQ5 replace my CRM?', a: 'No. It makes your existing CRM 10x more valuable by keeping it perfectly updated. Works with HubSpot, Salesforce, and Pipedrive.' },
              { q: 'How long does setup take?', a: 'Connect your CRM in 5 minutes. Full team onboarding in under a day.' },
              { q: 'Is my data safe?', a: 'AES-256 encryption, row-level tenant isolation, GDPR controls, and configurable data retention. EU-hosted.' },
              { q: 'What CRMs do you support?', a: 'HubSpot, Salesforce, and Pipedrive with identical feature parity across all three. You can also run NoQ5 standalone with a Google Drive mirror if you have no CRM yet.' },
              { q: 'Can I use NoQ5 without a CRM?', a: 'Yes. Start on our EU-hosted workspace with a live Google Drive mirror of every record. When you add HubSpot, Salesforce or Pipedrive later, we push the full history across.' },
              { q: 'How do I get started?', a: 'Contact us for a personalised demo. We offer trials on request so you can see the platform in action with your own data.' },
            ].map((faq, i) => (
              <div key={i} className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-16 text-center"
          >
            <div className="bg-primary rounded-sm p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
                Ready to stop doing admin?
              </h3>
              <p className="text-primary-foreground/80 mb-6 max-w-xl mx-auto">
                Join revenue teams who let AI handle the busywork so they can focus on selling.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" className="bg-background text-foreground hover:bg-background/90 font-bold px-8 py-6 text-lg" onClick={scrollToDemo}>
                  Contact Us <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
}
